import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { BibleStudy } from '../../../api/models/BibleStudy';
import { SectionModule } from '../../../api/models/BibleStudyModule';
import { routes } from '../../../route-list';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { ViewBibleStudyScreen } from '../components/ViewBibleStudyScreen';
import styled from 'styled-components';
import { colors } from '../../core/Styles';

export type ViewBibleStudyProps = {
  // Outputs
  /** the bible study loaded on mount */
  bibleStudy: BibleStudy | null;
  /** if bible study is currently being fetched */
  isLoadingStudy: boolean;
  /** The current module in View Module Modal */
  moduleInView: SectionModule | null;

  // Inputs
  /** takes user back to bible studies screen */
  userClickedBackButton: () => void;
  /** redirects user to edit current bible study */
  userClickedEditStudy: () => void;
  /** handler for when the user clicks "delete" from the "more" dropdown */
  userClickedDeleteStudy: () => void;
  /** Handler for when the user clicks on a module card to view it on a modal */
  userClickedOnModule: (module: SectionModule) => void;
  /** Handler for when user clicks out ot the View Module Modal to close it */
  userClickedOutModule: () => void;
  userToggledFeatureStatus: (payload: { isFeatured: boolean }) => void;
};

export const ViewBibleStudyContainer = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [moduleInView, setModuleInView] = useState<SectionModule | null>(null);
  const [study, setStudy] = useState<BibleStudy | null>(null);

  const request = useRequest();
  const history = useHistory();
  const params = useParams<{ id: string }>();

  // load bible study on mount
  const { loading: isLoadingStudy } = useAsyncRequest(
    useCallback(() => {
      return request<BibleStudy>(apiConfigs.getBibleStudy(params.id));
    }, [params.id, request]),
    useCallback(result => {
      setStudy(result);
    }, []),
    useCallback(() => {
      history.push(routes.BIBLE_STUDIES);
      toast.error('Program not found');
    }, [history])
  );

  return (
    <ViewBibleStudyScreen
      bibleStudy={study}
      moduleInView={moduleInView}
      isLoadingStudy={isLoadingStudy || isUpdating}
      userClickedOnModule={module => {
        setModuleInView(module);
      }}
      userClickedOutModule={() => {
        setModuleInView(null);
      }}
      userClickedBackButton={() => {
        history.push(routes.BIBLE_STUDIES);
      }}
      userClickedEditStudy={() => {
        history.push(routes.BIBLE_STUDIES__EDIT(params.id));
      }}
      userClickedDeleteStudy={() => {
        const studiersCount = study?.numberOfStudiers ?? 0;

        const onDelete = async () => {
          try {
            setIsUpdating(true);
            await request(apiConfigs.deleteProgram(params.id));
            setIsUpdating(false);
            toast.success('Study deleted!');
            history.replace(routes.BIBLE_STUDIES);
          } catch (e: any) {
            setIsUpdating(false);
            toast.error(e.message || 'Failed to delete study');
          }
        };

        const description =
          studiersCount > 0 ? (
            <WarningBox>
              <WarningTitle>This study has been used by {studiersCount} studiers</WarningTitle>
              <WarningBody>It may affect those studiers. Are you sure you want to delete this study?</WarningBody>
            </WarningBox>
          ) : (
            'Are you sure you want to delete this study?'
          );

        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description={description}
              onClose={options.onClose}
              onOk={async () => {
                options.onClose();
                await onDelete();
              }}
            />
          ),
        });
      }}
      userToggledFeatureStatus={async ({ isFeatured }) => {
        try {
          setIsUpdating(true);
          if (!study) throw new Error('Study was not yet loaded!');

          const payload = { ...study, isFeatured };
          await request(apiConfigs.putBibleStudy(payload));
          setStudy(payload);

          if (isFeatured) {
            toast.success(`${study.title} added to Featured!`);
          } else {
            toast.success(`${study.title} removed from Featured!`);
          }
        } catch (e: any) {
          toast.error(e.message || 'Failed to add to featured');
        } finally {
          setIsUpdating(false);
        }
      }}
    />
  );
};

const WarningBox = styled.div`
  background: ${colors.highlight};
  border: 1px solid rgba(85, 162, 233, 0.35);
  border-radius: 12px;
  padding: 14px 16px;
`;

const WarningTitle = styled.div`
  font-weight: 900;
  margin-bottom: 6px;
`;

const WarningBody = styled.div`
  font-weight: 700;
`;
